import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import React, { useMemo, useRef, useState } from 'react';
import type { Project } from 'api';

import {
  ClientGroupHeader,
  ClientGroupLabel,
  ProjectColorDot,
  ProjectPickerButton,
  ProjectPickerItem,
  ProjectPickerItemContent,
  ProjectPickerItemName,
  ProjectPickerList,
  ProjectPickerPopover,
  ProjectPickerSearch,
  projectPickerChevronStyle,
  projectPickerClientCountStyle,
  projectPickerFolderIconStyle,
  projectPickerFormButtonSx,
  projectPickerFormColorDotSx,
  projectPickerFormNameSx,
  projectPickerNoProjectNameStyle,
  projectPickerStarIconStyle,
} from '../tracker.styles';

interface ProjectGroup {
  clientName: string;
  projects: Project[];
}

interface ProjectPickerProps {
  projects: Project[];
  selectedId: string | null;
  onSelect: (project: Project) => void;
  /** Override Popper z-index (default 1400). Pass 1500 when inside a Dialog. */
  popperZIndex?: number;
  /** Render button as a full-width outlined form-field style */
  formVariant?: boolean;
}

export const ProjectPicker: React.FC<ProjectPickerProps> = ({
  projects,
  selectedId,
  onSelect,
  popperZIndex = 1400,
  formVariant = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsedClients, setCollapsedClients] = useState<Set<string>>(new Set());
  const anchorRef = useRef<HTMLButtonElement>(null);

  const selectedProject = projects?.find((p) => p.id === selectedId) ?? null;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q
      ? projects?.filter(
          (p) => p.name.toLowerCase().includes(q) || (p.clientName ?? '').toLowerCase().includes(q),
        )
      : projects;
  }, [projects, search]);

  const groups = useMemo<ProjectGroup[]>(() => {
    const map = new Map<string, Project[]>();
    for (const p of filtered) {
      const key = p.clientName ?? 'No Client';
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([clientName, ps]) => ({ clientName, projects: ps }));
  }, [filtered]);

  const toggleClient = (name: string) => {
    setCollapsedClients((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSelect = (p: Project) => {
    onSelect(p);
    setOpen(false);
    setSearch('');
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div>
        <ProjectPickerButton
          ref={anchorRef}
          variant={formVariant ? 'outlined' : 'text'}
          disableElevation
          onClick={() => setOpen((o) => !o)}
          active={selectedProject ? 1 : 0}
          sx={formVariant ? projectPickerFormButtonSx(open) : undefined}
        >
          {formVariant && selectedProject ? (
            <Box sx={{ ...projectPickerFormColorDotSx, bgcolor: selectedProject.color || '#9e9e9e' }} />
          ) : (
            <FolderOpenIcon style={projectPickerFolderIconStyle} />
          )}
          <Box component="span" sx={formVariant ? projectPickerFormNameSx : undefined}>
            {selectedProject ? selectedProject.name : 'Project'}
          </Box>
          {open ? (
            <ExpandLessIcon style={projectPickerChevronStyle} />
          ) : (
            <ExpandMoreIcon style={projectPickerChevronStyle} />
          )}
        </ProjectPickerButton>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: popperZIndex }}
        >
          <ProjectPickerPopover>
            <ProjectPickerSearch
              autoFocus
              placeholder="Find project or client"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <ProjectPickerList>
              {groups.map((group) => {
                const collapsed = collapsedClients.has(group.clientName);
                return (
                  <div key={group.clientName}>
                    <ClientGroupHeader onClick={() => toggleClient(group.clientName)}>
                      <ClientGroupLabel>{group.clientName}</ClientGroupLabel>
                      <ClientGroupLabel style={projectPickerClientCountStyle}>
                        {group.projects.length} project{group.projects.length !== 1 ? 's' : ''}
                      </ClientGroupLabel>
                      {collapsed ? (
                        <ExpandMoreIcon style={projectPickerStarIconStyle} />
                      ) : (
                        <ExpandLessIcon style={projectPickerStarIconStyle} />
                      )}
                    </ClientGroupHeader>

                    {!collapsed &&
                      group.projects.map((p) => (
                        <ProjectPickerItem
                          key={p.id}
                          selected={p.id === selectedId ? 1 : 0}
                          onClick={() => handleSelect(p)}
                        >
                          <ProjectPickerItemContent>
                            <ProjectColorDot dotcolor={p.color || '#9e9e9e'} />
                            <ProjectPickerItemName>{p.name}</ProjectPickerItemName>
                          </ProjectPickerItemContent>
                        </ProjectPickerItem>
                      ))}
                  </div>
                );
              })}
              {groups.length === 0 && (
                <ProjectPickerItem>
                  <ProjectPickerItemName style={projectPickerNoProjectNameStyle}>
                    No projects found
                  </ProjectPickerItemName>
                </ProjectPickerItem>
              )}
            </ProjectPickerList>
          </ProjectPickerPopover>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
